import { useRef, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, ImagePlus, FileText, Video, X } from 'lucide-react';

/**
 * Adjuntos de una FAQ (imágenes, PDF, video/YouTube).
 * Nunca propiedades — solo material del negocio.
 */
export default function FaqMediaFields({ faq, onChange, testId = 'faq-media' }) {
  const imgRef = useRef(null);
  const pdfRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const images = faq?.images || [];
  const pdfs = faq?.pdfs || [];
  const videos = faq?.videos || [];

  const patch = (field, value) => onChange({ ...faq, [field]: value });

  const upload = async (file, kind) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const path = kind === 'pdf' ? '/upload/file' : '/upload/image';
      const r = await api.post(path, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = r.data?.url;
      if (!url) return;
      if (kind === 'image') patch('images', [...images, url]);
      if (kind === 'pdf') patch('pdfs', [...pdfs, { url, title: file.name, kind: 'pdf' }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo subir');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 rounded-lg border border-white/5 p-2 bg-black/10" data-testid={testId}>
      <p className="text-[10px] text-muted-foreground">Adjuntos de esta FAQ (imágenes, PDF o video — no propiedades)</p>
      <div className="flex flex-wrap gap-1">
        {images.map((url, i) => (
          <span key={`img-${i}`} className="inline-flex items-center gap-1 text-[10px] bg-white/5 border border-white/10 rounded px-1.5 py-0.5">
            img {i + 1}
            <button type="button" onClick={() => patch('images', images.filter((_, n) => n !== i))}><X className="w-3 h-3" /></button>
          </span>
        ))}
        {pdfs.map((p, i) => (
          <span key={`pdf-${i}`} className="inline-flex items-center gap-1 text-[10px] bg-white/5 border border-white/10 rounded px-1.5 py-0.5">
            PDF {p.title || i + 1}
            <button type="button" onClick={() => patch('pdfs', pdfs.filter((_, n) => n !== i))}><X className="w-3 h-3" /></button>
          </span>
        ))}
        {videos.map((v, i) => (
          <span key={`vid-${i}`} className="inline-flex items-center gap-1 text-[10px] bg-white/5 border border-white/10 rounded px-1.5 py-0.5 max-w-[180px] truncate">
            {v.url || v}
            <button type="button" onClick={() => patch('videos', videos.filter((_, n) => n !== i))}><X className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <Button type="button" size="sm" variant="outline" className="h-7 text-[10px]" disabled={busy} onClick={() => imgRef.current?.click()}>
          {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ImagePlus className="w-3 h-3 mr-1" />}Imagen
        </Button>
        <Button type="button" size="sm" variant="outline" className="h-7 text-[10px]" disabled={busy} onClick={() => pdfRef.current?.click()}>
          <FileText className="w-3 h-3 mr-1" />PDF
        </Button>
        <div className="flex-1 min-w-[160px]">
          <Label className="text-[9px] text-muted-foreground">Video / YouTube</Label>
          <Input
            className="h-7 bg-black/20 border-white/10 text-[10px]"
            placeholder="https://youtu.be/..."
            data-testid={`${testId}-video-url`}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                const url = e.currentTarget.value.trim();
                if (url.startsWith('http')) {
                  patch('videos', [...videos, { url, title: '', kind: 'video' }]);
                  e.currentTarget.value = '';
                }
              }
            }}
          />
        </div>
        <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0], 'image')} />
        <input ref={pdfRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0], 'pdf')} />
      </div>
    </div>
  );
}
